import { useParams, Link } from 'react-router-dom'
import { useMemo } from 'react'
import { roles } from '../data/roles.ts'
import { skills } from '../data/skills.ts'
import { calculateSkillsROI, formatCurrency, formatPercent } from '../utils/calculations.ts'
import SeoH1 from '../components/SeoH1.tsx'

export default function SkillPage() {
  const { skillId } = useParams<{ skillId: string }>()
  const skill = useMemo(() => skills.find(s => s.id === skillId) || skills[0], [skillId])
  const role = roles[0]

  const result = useMemo(() => calculateSkillsROI(skill, {
    role,
    skillId: skill.id,
    learningPath: 'self-study',
    weeklyHours: 5,
    currentSalary: role.baseSalary,
    remainingYears: 10
  }), [skill, role])

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <SeoH1 fallback={`${skill.name} ROI Calculator`} className="text-3xl font-bold text-slate-900 mb-3" />
        <p className="text-slate-600 max-w-2xl">
          Learn {skill.name} in about {result.timeToAcquireMonths.toFixed(0)} months at 5 hours/week.
          The total investment is {formatCurrency(result.totalInvestment)} and the 10-year ROI is
          {formatPercent(result.roiPercent / 100)}.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card p-4">
          <div className="metric-label">Time to acquire</div>
          <div className="metric-value">{result.timeToAcquireMonths.toFixed(0)} months</div>
        </div>
        <div className="card p-4">
          <div className="metric-label">Total investment</div>
          <div className="metric-value">{formatCurrency(result.totalInvestment)}</div>
        </div>
        <div className="card p-4">
          <div className="metric-label">Annual salary premium</div>
          <div className="metric-value text-emerald-600">{formatCurrency(result.annualPremium)}</div>
        </div>
        <div className="card p-4">
          <div className="metric-label">Payback period</div>
          <div className="metric-value">{result.paybackMonths.toFixed(0)} months</div>
        </div>
      </div>

      <div className="card p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Who benefits most</h2>
        <p className="text-slate-600 mb-4">
          This skill has the highest salary premium for roles in these categories:
        </p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(skill.premiumByCategory)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([category, premium]) => (
              <span key={category} className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium">
                {category}: {(premium * 100).toFixed(0)}%
              </span>
            ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <Link to={`/skills-roi?skill=${skill.id}`} className="btn-primary">
          Personalize for your role
        </Link>
        <Link to="/hub" className="btn-secondary">
          Browse more skills
        </Link>
      </div>
    </div>
  )
}
